import React, { useEffect, useState } from "react";
import { usePayment, useTheme } from "../hooks";
import { Box, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import Button from "./button";
import { useQueryCustomer } from "../api/queries";
import { getFailureDisplay, getStorageToken, isErrorResponse } from "../utils";
import LoadingPage from "./loadingPage";
import { Customer } from "../models/Service";
import { getContrastTextColor } from "../utils/colorManager";
import TextInput from "./inputs/TextInput";

const SearchCustomerForm = ({ corporateId }: { corporateId: string }) => {
  const { t, i18n } = useTranslation();
  const { setError, setCustomerUser, setCurrentStep, currentStep } =
    usePayment();
  const [active, setActive] = useState(false);
  const [nameError, setNameError] = useState("");
  const [customerCode, setCustomerCode] = useState("");
  const { theme } = useTheme();

  const token = getStorageToken();

  const { data, isLoading, isFetching, isError, refetch, isSuccess } =
    useQueryCustomer({
      customerCode,
      corporateId,
      enabled: !!token && active,
      authorisation: token || "",
    });

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomerCode(value);

    if (value.trim() === "") {
      setNameError(t("payment.form.errors.name_required", "Le nom est requis"));
    } else {
      setNameError("");
    }
  };
  const onSubmit = () => {
    setError("");
    refetch();
  };

  useEffect(() => {
    // A rejected lookup keeps the previous `data`, so bail out before the
    // success branch can advance the step on a failed search.
    if (isError) {
      setError(t("errors.unknown_error"));
      return;
    }

    if (isErrorResponse(data)) {
      // Was the envelope's developer message -- server-side-only OPS text
      // rendered straight to the payer, with no resolver in between.
      setError(getFailureDisplay(data, t, i18n.language).message);
      return;
    }

    if (isSuccess && !isErrorResponse(data)) {
      setCustomerUser(data as Customer);
      setCurrentStep(currentStep + 1);
    }
  }, [data, isError, i18n.language]);

  return (
    <Box mb={4}>
      {isFetching && (
        <LoadingPage message="Retrieving your account information" />
      )}
      <Grid
        container
        spacing={3}
        sx={{ alignItems: "center", display: "flex", justifyContent: "center" }}
      >
        <Grid item xs={12} md={8} sx={{ alignItems: "center" }}>
          <TextInput
            fullWidth
            type="text"
            id="name"
            label={t("payment.form.customer_code")}
            value={customerCode}
            onChange={handleNameChange}
            error={!!nameError}
            helperText={
              nameError ? nameError : t("payment.form.customer_code_hint")
            }
            variant="outlined"
            size="medium"
          />
        </Grid>
      </Grid>
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          justifyContent: "center",
          paddingTop: 4,
          px: 15,
        }}
      >
        <Button
          variant="contained"
          isLoading={isFetching}
          sx={{
            backgroundColor: theme?.color_primary,
            color: getContrastTextColor(theme.color_primary),
          }}
          disabled={!customerCode}
          fullWidth
          size="large"
          onClick={() => onSubmit()}
        >
          {t("payment.form.search_btn")}
        </Button>
      </Box>
    </Box>
  );
};

export default SearchCustomerForm;
